import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MessageCircle, Heart, Lightbulb, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface FutureSelfLetterProps {
  onClose: () => void;
  initialData?: {
    future_self_message?: string;
    future_self_guidance?: string;
  };
}

export default function FutureSelfLetter({ onClose, initialData }: FutureSelfLetterProps) {
  const [futureSelfMessage, setFutureSelfMessage] = useState(initialData?.future_self_message || '');
  const [futureSelfGuidance, setFutureSelfGuidance] = useState(initialData?.future_self_guidance || '');
  const [showOptionalGuidance, setShowOptionalGuidance] = useState(false);

  // Show optional guidance after 7pm or if explicitly requested
  useEffect(() => {
    const currentHour = new Date().getHours();
    setShowOptionalGuidance(currentHour >= 19);
  }, []);

  const handleSave = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const today = new Date().toISOString().split('T')[0];
      
      const saveData = {
        user_id: user.id,
        date: today,
        future_self_message: futureSelfMessage,
        future_self_guidance: futureSelfGuidance,
      };

      const { error } = await supabase
        .from('daily_checkins')
        .upsert(saveData, {
          onConflict: 'user_id,date'
        });

      if (error) throw error;
      
      toast({
        title: "Success!",
        description: "Your Future Self letter has been saved.",
      });
      onClose();
    } catch (error) {
      console.error('Error saving Future Self letter:', error);
      toast({
        title: "Error",
        description: "Failed to save letter. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Letter from Your Future Self
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Why This Practice Matters */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-lg border">
            <div className="flex items-start gap-3">
              <Heart className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">Why Write to Your Future Self?</h3>
                <p className="text-sm text-muted-foreground">
                  This practice bridges the gap between who you are now and who you're becoming. Your Future Self already exists as a potential-they have the wisdom, confidence, and clarity you're developing. 
                </p>
                <p className="text-sm text-muted-foreground">
                  When you write from this elevated perspective, you're not just journalling-you're channelling guidance from the version of you who has already walked this path and overcome these challenges.
                </p>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-gradient-to-r from-secondary/10 to-accent/10 p-6 rounded-lg border">
            <div className="flex items-start gap-3">
              <Lightbulb className="h-5 w-5 text-secondary mt-1 flex-shrink-0" />
              <div className="space-y-3">
                <h3 className="font-semibold text-foreground">How to Channel Your Future Self</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p><strong>Step into their energy:</strong> Imagine yourself 6 months or 1 year from now, having achieved your current goals.</p>
                  <p><strong>Feel their presence:</strong> This version of you is calm, wise, and loving. They remember being where you are now.</p>
                  <p><strong>Let them speak:</strong> What would they want you to know? What encouragement would they offer? What perspective would help you most right now?</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Letter Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              <Label className="text-base font-medium">Your Letter from Future Self</Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Channel a message from the version of yourself who has already achieved what you're working toward. What wisdom, encouragement, or perspective would they share with you today?
            </p>
            <Textarea
              placeholder="Dear Present Self,&#10;&#10;I want you to know that the path you're walking today is leading you exactly where you need to be. The small actions you're taking, the consciousness you're building, the courage you're showing-it's all creating the foundation for everything that's coming.&#10;&#10;I remember feeling uncertain about [specific challenge you're facing]. What I wish I could tell you then is that this challenge is actually preparing you for [future strength/skill]. Trust the process, even when you can't see the full picture.&#10;&#10;Keep going. You're closer than you think.&#10;&#10;With infinite love and gratitude,&#10;Your Future Self"
              value={futureSelfMessage}
              onChange={(e) => setFutureSelfMessage(e.target.value)}
              rows={12}
              className="font-serif italic text-base leading-relaxed"
            />
          </div>

          {/* Optional Evening Guidance */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">💌 Evening Guidance (Optional)</Label>
              {!showOptionalGuidance && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowOptionalGuidance(true)}
                >
                  Add Evening Reflection
                </Button>
              )}
            </div>
            
            {showOptionalGuidance && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Available after 7pm or anytime you need extra guidance. What specific wisdom would your Future Self share about today's experiences?
                </p>
                <Textarea
                  placeholder="Today was [describe your day]. What I want you to remember is...&#10;&#10;Tomorrow, focus on...&#10;&#10;You're doing better than you realise because..."
                  value={futureSelfGuidance}
                  onChange={(e) => setFutureSelfGuidance(e.target.value)}
                  rows={6}
                  className="font-serif italic"
                />
              </div>
            )}
          </div>

        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button onClick={handleSave} className="flex-1">
          Save Letter
        </Button>
        <Button variant="outline" onClick={onClose}>
          Back to Spotlight
        </Button>
      </div>
    </div>
  );
}